/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Language = "en" | "it";
export type Localized<T = string> = Record<Language, T>;

type I18nContextValue = {
  language: Language;
  locale: "en-GB" | "it-IT";
  setLanguage: (language: Language) => void;
  text: <T,>(value: Localized<T>) => T;
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  currency: (value: number, options?: Intl.NumberFormatOptions) => string;
  date: (value: string | Date, options?: Intl.DateTimeFormatOptions) => string;
};

const LANGUAGE_KEY = "mde:language:v1";
const I18nContext = createContext<I18nContextValue | null>(null);

export function withDefaultDateParts(options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
  if (options && Object.keys(options).length > 0) return options;
  return { day: "2-digit", month: "short", year: "numeric" };
}

function initialLanguage(): Language {
  if (typeof window === "undefined") return "en";
  const query = new URLSearchParams(window.location.search);
  if (query.get("preview") === "1" && (query.get("lang") === "en" || query.get("lang") === "it")) return query.get("lang") as Language;
  const saved = window.localStorage.getItem(LANGUAGE_KEY);
  if (saved === "en" || saved === "it") return saved;
  return window.navigator.language.toLowerCase().startsWith("it") ? "it" : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);
  const locale = language === "it" ? "it-IT" : "en-GB";

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem(LANGUAGE_KEY, language);
  }, [language]);

  const value = useMemo<I18nContextValue>(() => ({
    language,
    locale,
    setLanguage: setLanguageState,
    text: <T,>(localized: Localized<T>) => localized[language] ?? localized.en,
    number: (amount, options) => new Intl.NumberFormat(locale, options).format(amount),
    currency: (amount, options) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0, ...options }).format(amount),
    date: (value, options) => new Intl.DateTimeFormat(locale, withDefaultDateParts(options)).format(typeof value === "string" ? new Date(value) : value),
  }), [language, locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}

export const localized = <T,>(en: T, it: T): Localized<T> => ({ en, it });
